import { DataGridFeatureConfig } from '@waseel-asp/tpa-waseel-lib-staging';
import { defaultGridFeatures } from "../../../../../../shared/constants/grid-feature-config";
import cloneDeep from 'lodash/cloneDeep';
import { IBeneficiaryDocument } from "../../../beneficiary/models/beneficiary.model";

const gridFeatures: DataGridFeatureConfig<IPolicy | IPolicyBeneficiary> = cloneDeep(defaultGridFeatures);

export const primaryGridFeatures: DataGridFeatureConfig<IPolicy | IPolicyBeneficiary> = {
    ...gridFeatures,
    allowSearching: false,
    isPersistanceStorage: false
};

export const childGridFeatures: DataGridFeatureConfig<IPolicyBeneficiary> = {
    ...gridFeatures,
    showPagination: false,
    enableApiSorting: false,
    enableApiSearching: false,
    allowSorting: false,
    isDummyColumn: true,
    allowHideShowColumns: false,
    hideTableHeader: false,
    isDefaultEditEnabled: true,
    isPersistanceStorage: false
};

interface IPolicyNetwork {
    networkId: string;
    codeEnglish: string;
    codeArabic: string;
    nameEnglish: string;
    nameArabic: string;
    descriptionEnglish: string;
    descriptionArabic: string;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
}

interface IAddress {
    addressId: string;
    countryId: string;
    stateId: string;
    cityId: string;
    countryNameEnglish: string;
    countryNameArabic: string;
    stateNameEnglish: string;
    stateNameArabic: string;
    cityNameEnglish: string;
    cityNameArabic: string;
    street: string;
    addressLine: string;
    zipcode: string;
    latitude: string;
    longitude: string;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
}

interface IContact {
    contactId: string;
    telephone: string;
    mobileNumber: string;
    contactNumber: string;
    emergencyNumber: string;
    fax: string;
    email: string;
    website: string;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
}

export interface IPolicyBeneficiary {
    beneficiaryId: string;
    firstName: string;
    middleName: string;
    lastName: string;
    familyName: string;
    dob: string;
    gender: string;
    nationalityId: string;
    nationalityNameEnglish?: string;
    nationalityNameArabic?: string;
    bloodGroupId: string;
    bloodGroupNameEnglish?: string;
    bloodGroupNameArabic?: string;
    preferredLanguageId: string;
    languageNameEnglish?: string;
    languageNameArabic?: string;
    documentTypeNameEnglish?: string;
    beneficiaryDocuments: IBeneficiaryDocument[];
    documentTypeNameArabic?: string;
    patientFileNo: string;
    passportNumber: string;
    visaNumber: string;
    borderNumber: string;
    visaTypeId: string;
    visaTypeNameEnglish?: string;
    visaTypeNameArabic?: string;
    visitTitle?: string;
    visaExpiryDate: string;
    memberId: string;
    fullName?: string;
    address?: IAddress;
    contact?: IContact;
    isPrimaryBeneficiary: boolean;
    relationId: string;
    relationNameEnglish: string;
    relationNameArabic: string;
    parentBeneficiaryId: string;
    dummyColumn?: string;
    classId?: string;
    classNameEnglish?: string;
    classNameArabic?: string;
    maritalStatusNameEnglish?: string;
    maritalStatusNameArabic?: string;
    memberSince?: string;
    policyAttachment?: IPolicyAttachment;
}

export interface IPolicyAttachment {
    policyAttachmentId: string;
    attachmentName: string;
    attachmentType: string;
    beneficiaryId: string;
    fileSize: number | null;
}

interface PolicySubCoverage {
    subCoveragesId: string;
    codeEnglish: string;
    codeArabic: string;
    descriptionEnglish: string;
    descriptionArabic: string;
    subCoverageLimit: number;
    approvalThreshold: number;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
}

interface IPolicyBenefit {
    benefitId: string;
    codeEnglish: string;
    codeArabic: string;
    descriptionEnglish: string;
    descriptionArabic: string;
    benefitLimit: number;
    patientShareType: string;
    patientShareTypeNameEnglish: string;
    patientShareTypeNameArabic: string;
    qtyLimit: number;
    qtyFrequency: number;
    qtyFrequencyUnitId: string;
    qtyFrequencyUnitNameEnglish: string;
    qtyFrequencyUnitNameArabic: string;
    maxLimit: number;
    maxLimitFrequency: number;
    maxLimitFrequencyUnitId: string;
    maxLimitFrequencyUnitNameEnglish: string;
    maxLimitFrequencyUnitNameArabic: string;
    maxPatientShare: number;
    maxConsultationFee: number;
    approvalThreshold: number;
    caseTypeId: string;
    caseTypeNameEnglish: string;
    caseTypeNameArabic: string;
    casePatientShare: number;
    caseMaxPatientShare: number;
    caseMaxConsultFees: number;
    caseApprovalThreshold: number;
    coverage: string;
    exclusions: string;
    comments: string;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
    policySubCoverages: PolicySubCoverage[];
}

interface IPolicyClass {
    classId: string;
    classCodeEnglish: string;
    classCodeArabic: string;
    classNameEnglish: string;
    classNameArabic: string;
    classLimit: number;
    roomTypeId: string;
    roomTypeNameEnglish: string;
    roomTypeNameArabic: string;
    roomLimit: number;
    coverage: string;
    exclusions: string;
    comments: string;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
    policyBenefits: IPolicyBenefit[];
    policyNetwork: INetworkDetails;
}
interface INetworkDetails {
    networkId: string;
    codeEnglish: string;
    codeArabic: string;
    nameEnglish: string;
    nameArabic: string;
    descriptionEnglish: string;
    descriptionArabic: string;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
}

export interface IPolicy {
    policyId: string;
    insuranceCompanyId: string;
    insuranceCompanyNameEnglish: string;
    insuranceCompanyNameArabic: string;
    policyTypeId: string
    policyTypeNameArabic: string
    policyTypeNameEnglish: string
    policyNumber: string;
    policyName: string;
    policyGroupName: string;
    policyContactName: string;
    paymentFrequencyId: string;
    paymentMethodId: string;
    paymentFrequencyNameEnglish: string;
    paymentFrequencyNameArabic: string;
    paymentMethodNameEnglish: string;
    paymentMethodNameArabic: string;
    policyHolderFirstName: string;
    policyHolderMiddleName: string;
    policyHolderLastName: string;
    policyHolderName: string;
    policyHolderFamilyName: string;
    policyHolderFullName: string;
    issueDate: string;
    startDate: string;
    endDate: string;
    endorsementNo: number;
    policyStatusId: string;
    policyStatusNameEnglish: string;
    policyStatusNameArabic: string;
    policyQuotation: number;
    policyPremium: number;
    coverageTypeId: string;
    coverageTypeNameEnglish: string;
    coverageTypeNameArabic: string;
    billingTypeId: string;
    billingTypeNameEnglish: string;
    billingTypeNameArabic: string;
    coverage: string;
    exclusions: string;
    comments: string;
    additionalInformation: string;
    deleted: boolean;
    createdBy: string;
    createdDate: string;
    lastUpdatedBy: string;
    lastUpdatedDate: string;
    deletedBy: string;
    deletedDate: string;
    address: IAddress;
    addressId: string;
    contact: IContact;
    contactId: string;
    policyNetworks: IPolicyNetwork[];
    policyBeneficiaries: IPolicyBeneficiary[];
    policyClassNetworks: IPolicyClass[];
    policyClasses: IPolicyClass[];
    isPrimaryBeneficiary?: boolean;
    beneficiaryId?: string;
    erpPolicyId?: string;
}

export interface Network {
    networkId: string;
    codeEnglish: string;
    codeArabic: string;
}

export interface PolicyClass {
    policyClassId: string;
    policyId: string;
    classId: string;
    classNameEnglish: string;
    classNameArabic: string;
    classCodeEnglish: string;
    classCodeArabic: string;
    classLimit: number;
    roomTypeId: string;
    roomTypeNameEnglish: string;
    roomTypeNameArabic: string;
    roomLimit: number;
    coverage: string;
    exclusions: string;
    comments: string;
    network: Network
    classCaseTypes: ClassCaseTypes[];
}

interface ClassCaseTypes {
    caseTypeId: string;
    caseTypeNameEnglish: string;
    caseTypeNameArabic: string;
    coverageCondition: string;
    classBenefits: ClassBenefits[];
}

interface ClassBenefits {
    coverageCondition: string;
    benefitSubCoverages: benefitSubCoverages[],
    benefitId: string;
    codeEnglish: string;
    codeArabic: string;
    descriptionEnglish: string;
    descriptionArabic: string;
    benefitLimit: number;
    patientShareType: string;
    patientShareTypeDesc: string;
    patientShare: string;
    maxPatientShare: number;
    maxConsultationFee: number;
    approvalThreshold: number;
    qtyLimit: number;
    qtyFrequency: number;
    qtyFrequencyUnitId: string;
    qtyFrequencyUnitNameEnglish: string;
    qtyFrequencyUnitNameArabic: string;
    maxLimit: number;
    maxLimitFrequency: number;
    maxLimitFrequencyUnitId: string;
    maxLimitFrequencyUnitNameEnglish: string;
    maxLimitFrequencyUnitNameArabic: string;
    coverage: string;
    exclusions: string;
    comments: string;
}

interface benefitSubCoverages {
    coverageCondition: string;
    subCoveragesId: string;
    benefitId: string;
    codeEnglish: string;
    codeArabic: string;
    descriptionEnglish: string;
    descriptionArabic: string;
    subCoverageLimit: number;
    approvalThreshold: number;
}

export interface Company {
    companyId: string;
    nameEnglish: string;
    nameArabic: string;
}

export const PolicyBeneficiary = {
    entityName: 'POLICY.POLICY_BENEFICIARY'
};

export const policyConfig = {
    entityName: 'POLICY.TITLE',
    policyNumber: 'POLICY.POLICY_AND_PRODUCT.POLICY_NO',
    policyName: 'POLICY.POLICY_AND_PRODUCT.POLICY_NAME',
    policyGroupName: 'POLICY.POLICY_AND_PRODUCT.POLICY_GROUP_NAME',
    policyContactName: 'POLICY.POLICY_AND_PRODUCT.POLICY_CONTACT_NAME',
    policyHolderName: 'POLICY.POLICY_AND_PRODUCT.HOLDER_NAME',
    endorsementNo: 'FORM_FIELD.ENDORSEMENT_NUMBER',
    policyQuotation: 'POLICY.POLICY_AND_PRODUCT.QUOTATION',
    policyPremium: 'POLICY.POLICY_AND_PRODUCT.PREMIUM',
    coverage: 'FORM_FIELD.COVERAGE',
    comments: 'FORM_FIELD.COMMENTS',
    additionalInformation: 'FORM_FIELD.ADDITIONAL_INFO',
    exclusions: 'FORM_FIELD.EXCLUSION',
    insuranceComapanyId: 'INSURANCE_COMPANY.TITLE',
    policyStatusId: 'FORM_FIELD.STATUS',
    beneficiaryId: 'BENEFICIARY.TITLE',
    policyNetworkIds: 'CONFIGURATION.NETWORK.TITLE',
    policyClassId: 'CONFIGURATION.POLICY_CLASS.TITLE',
    policyBenefitId: 'POLICY.POLICY_AND_PRODUCT.BENEFITS',
    policySubCoverageIds: 'POLICY.POLICY_AND_PRODUCT.SUBCOVERAGES',
    isPrimaryBeneficiary: 'POLICY.POLICY_AND_PRODUCT.BENEFICIARY',
    issueDate: 'FORM_FIELD.ISSUE_DATE',
    startDate: 'FORM_FIELD.START_DATE',
    endDate: 'FORM_FIELD.END_DATE',
    lastUpdatedDate: 'FORM_FIELD.LAST_UPDATED_DATE',
    policyType: 'CONFIGURATION.MASTER_DATA.POLICY_TYPE.TITLE',
    coverageType: 'FORM_FIELD.COVERAGE_TYPE',
    BillingType: 'POLICY.POLICY_AND_PRODUCT.BILLING_TYPE',
};
//ts-prune-ignore-next
export interface ICustomePolicyClasses {
    customPolicyClassId: number,
    policyClass: []
}

//ts-prune-ignore-next
export type PolicyClassMode = 'edit' | 'view';